//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, CircularProgress, Grid, IconButton, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import ViewLogo from '@react/components/elements/ViewLogo'
import { theme } from '@react/components/theme'
import { companyProfileSignedAction, fetchCompanyProfileExists } from '@react/fetchers/companyProfileFetchers'
import { chainActions } from '@react/redux/chainSlice'
import { navActions } from '@react/redux/navSlice'
import { poolActions } from '@react/redux/poolSlice'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import {
  CompanyProfileActionCTO,
  CompanyProfileActionEnum,
  CompanyProfileCTO,
  CompanyProfileCTOSchema,
} from '@shared/CompanyProfileCTO'
import { MESSAGE_TO_SIGN } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { EthAddr } from '@shared/SchemaTypes'
import { v4 as uuidv4 } from 'uuid'

export default function CompanyProfileSelfRegistryForm(props: {
  companyProfileCTO?: CompanyProfileCTO
  companyProfiles: CompanyProfileCTO[]
  accountAddr: EthAddr
  closeCallback?: () => void
}): ReactElement {
  const { companyProfileCTO, accountAddr, closeCallback } = props

  const {
    userConnection: { signedMessage, signer },
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()

  const dispatch = useOtDispatch()
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [isValid, setIsValid] = useState<boolean>(false)
  const [companyName, setCompanyName] = React.useState<string>(companyProfileCTO?.companyName ?? '')
  const [emails, setEmails] = React.useState<string[]>(companyProfileCTO?.emails ?? [])

  const [accessCodeSpinner, setAccessCodeSpinner] = React.useState<boolean>(false)
  const [validationMessage, setValidationMessage] = React.useState<string>('')
  const [accessCodeUuid, setAccessCodeUuid] = React.useState<string>('')
  const [accessCodeSuccess, setAccessCodeSuccess] = React.useState<boolean>(false)
  const [originatorUuid, setOriginatorUuid] = React.useState<string>('')
  const [uuid] = React.useState<string>(companyProfileCTO?.uuid ?? uuidv4())

  const handleAccessCodeRegister = () => {
    setAccessCodeSpinner(true)
    if (accessCodeUuid === 'OpenTrade') {
      setOriginatorUuid(accessCodeUuid)
      setAccessCodeSuccess(true)
      setAccessCodeSpinner(false)
    } else {
      fetchCompanyProfileExists(serverApiBaseUrl, restAPIKey, accessCodeUuid)
        .then((exists) => {
          if (exists) {
            logOT.info('accessCodeUuid exists', { accessCodeUuid })
            setOriginatorUuid(accessCodeUuid.trim())
            setAccessCodeSuccess(true)
            setAccessCodeSpinner(false)
          } else {
            logOT.info('accessCodeUuid Not found', { accessCodeUuid })
            setAccessCodeUuid('')
            setAccessCodeSuccess(false)
            setAccessCodeSpinner(false)
          }
        })
        .catch((error) => {
          logOT.error('handleAccessCodeRegister error', error)
          setAccessCodeSpinner(false)
        })
    }
  }
  const handleOnSave = () => {
    logOT.info('handleOnSave')

    const handleOnSaveAsync = async () => {
      if (serverApiBaseUrl && signedMessage && accountAddr) {
        setIsSubmitted(true)

        const companyProfileActionCTO: CompanyProfileActionCTO = {
          action: CompanyProfileActionEnum.SELF_REGISTRATION,
          originatorUuid,
          companyName,
          emails: [...emails.map((e) => e.trim())],
          wallets: [accountAddr],
          uuid,
          instructions: '',
          requestedPoolsAccess: [],
        }
        logOT.info('companyProfileActionCTO', { companyProfileActionCTO, signedMessage })
        const savedProfile = await companyProfileSignedAction(
          serverApiBaseUrl,
          companyProfileActionCTO,
          restAPIKey,
          signedMessage,
          accountAddr
        )
        logOT.info('savedProfile', { savedProfile })
        // Update Redux store with new company profile
        dispatch(chainActions.updateCompanyProfile(savedProfile))

        dispatch(poolActions.updateCompanyProfile(savedProfile))
        setIsSubmitted(false)
        dispatch(navActions.navigateTo({ url: `/companyProfile` }))
        const event = new Event('modalClose')
        logOT.info('dispatching modalClose')
        window.dispatchEvent(event)
        closeCallback?.()
      }
    }
    handleOnSaveAsync().catch((error) => {
      logOT.error('handleOnSave error', error)
      setIsSubmitted(false)
    })
  }
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
  }
  useEffect(() => {
    validate()
  }, [companyName, emails])
  const [errorSigning, setErrorSigning] = useState(false)
  useEffect(() => {
    if (signer && !signedMessage) {
      const asyncSignMessage = async () => {
        logOT.info('SignMessage ,CompanyProfileSelfRegistryForm 1')
        const _signedMessage = await signer.signMessage(MESSAGE_TO_SIGN())
        logOT.info('asyncSignMessage ', MESSAGE_TO_SIGN(), _signedMessage)
        dispatch(chainActions.updateSignedMessage(_signedMessage))
      }
      if (!errorSigning) {
        asyncSignMessage().catch((error) => {
          setErrorSigning(true)
          logOT.error('asyncSignMessage error', error)
        })
      }
    }
  }, [signer, signedMessage])

  function validate() {
    const companyProfileCTO: CompanyProfileCTO = {
      companyName,
      isIssuer: false,
      emails: emails.map((e) => e.trim()),
      wallets: [accountAddr],
      uuid,
    }

    for (let i = 0; i < (props.companyProfiles?.length ?? 0); i++) {
      if (props.companyProfiles && props.companyProfiles[i].uuid !== companyProfileCTO.uuid) {
        for (const addr of props.companyProfiles[i].wallets) {
          logOT.info('Check duplicate addr ', addr)
          if (companyProfileCTO.wallets.includes(addr)) {
            logOT.info('Wallet already exists ' + addr + ' in ' + props.companyProfiles[i].companyName)
            setValidationMessage('Wallet already exists ' + addr + ' in ' + props.companyProfiles[i].companyName)
            setIsValid(false)
            return
          }
        }
      }
    }
    try {
      const validation = CompanyProfileCTOSchema.validateSync(companyProfileCTO)
      setIsValid(true)
      setValidationMessage('')
      logOT.info('validation', validation)
    } catch (e: unknown) {
      const error = e as { message: string }
      setValidationMessage(error.message)

      setIsValid(false)
    }
  }
  if (errorSigning) {
    return <div>Prevented from signing message with your wallet</div>
  }
  if (!signedMessage) {
    return <div>Sign the message with your wallet to continue</div>
  }
  if (!accessCodeSuccess) {
    return (
      <div data-testid="Company Profile Form">
        <Grid alignItems={'stretch'} container spacing={4} sx={{ flexGrow: 1, marginBottom: 8 }} py={2}>
          <Grid item xs={12}>
            <InputLabel htmlFor="accessCodeUuid">Access Code To Register</InputLabel>
            <TextField
              id="accessCodeUuid"
              data-testid="accessCodeUuid"
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Access Id"
              value={accessCodeUuid}
              onChange={(event) => {
                setAccessCodeUuid(event.target.value)
              }}
            />
          </Grid>
          {accessCodeSpinner && (
            <Grid item xs={12}>
              <CircularProgress />
            </Grid>
          )}
          <Grid item xs={12}>
            <Button disabled={accessCodeSpinner} variant="contained" color="primary" onClick={handleAccessCodeRegister}>
              Register
            </Button>
          </Grid>
        </Grid>
      </div>
    )
  }
  return (
    <div data-testid="Company Profile Form">
      <Grid alignItems={'stretch'} container spacing={4} sx={{ flexGrow: 1, marginBottom: 8 }} py={2}>
        <Grid item xs={12}>
          <Typography>UUID2 = {uuid}</Typography>
          <ViewLogo size={50} addrOrId={uuid}></ViewLogo>
        </Grid>

        <Grid item xs={12}>
          <InputLabel htmlFor="companyName">Company Name</InputLabel>
          <TextField
            id="companyName"
            data-testid="companyName"
            sx={{ width: '300px' }}
            variant="outlined"
            placeholder="Enter Company Name"
            value={companyName}
            onChange={(event) => {
              setCompanyName(event.target.value)
            }}
          />
        </Grid>
        {accountAddr && (
          <Grid item xs={12}>
            <Typography>Account Address = {accountAddr}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <InputLabel htmlFor="emails">Email Addresses</InputLabel>
          {emails.map((email, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                id={`email-${index}`}
                data-testid={`email-${index}`}
                sx={{ width: '300px' }}
                variant="outlined"
                value={email}
                onChange={(event) => {
                  const newEmails = [...emails]
                  newEmails[index] = event.target.value
                  setEmails(newEmails)
                }}
              />
              <IconButton
                onClick={() => {
                  const newEmails = emails.filter((_, i) => i !== index)
                  setEmails(newEmails)
                }}
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            onClick={() => {
              setEmails([...emails, ''])
            }}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Email
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography style={{ color: 'red' }}>{validationMessage}</Typography>
        </Grid>
        {isSubmitted && (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        )}
      </Grid>

      <Button disabled={isSubmitted || !isValid} sx={sx} onClick={handleOnSave} variant={'contained'}>
        <Typography variant={'buttonBold'}>Save</Typography>
      </Button>
    </div>
  )
}
